import { privateSelectors } from './nv-feedback-private.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — «UI nuova» in-app feedback (FEEDBACK-PLAN.md): the
 *              user's last steps before a feedback (last 5 pages + last 10
 *              clicks), read by nv-feedback-send for the «Ultime azioni» line
 *   extends:   -
 *   template:  none
 *   exports:   installFeedbackTrail(router) (idempotent), uninstallFeedbackTrail(),
 *              feedbackTrail() → { routes: [{ url, name, at }], clicks: [{ label, part, at }] },
 *              clickTarget(el), elementLabel(el, { isPrivate }), isPrivateElement(el, { isClinic }),
 *              isPrivateTarget(el, { isClinic }) (shared with nv-feedback-describe)
 *   uses:      router service `routeDidChange`, document click (capture phase),
 *              lib/nv-feedback-private (privateSelectors: no label from a private zone)
 *   siblings:  nv-feedback-describe.js, nv-feedback-send.js; installed by
 *              services/ui.js apply() while the nuova UI is active
 *   tests:     medical tests/unit/lib/nv-feedback-trail (unit)
 *   gotchas:   (1) PRIVACY: labels only, never an input/textarea/select value or
 *              typed text; the page URL is kept WITHOUT its query string (search
 *              terms, tokens); a click inside a private zone (clinic-aware
 *              privateSelectors, always the clinic superset here: the trail does
 *              not know the tenant) keeps only its data-ui-part / tag. (2) Clicks
 *              inside the feedback overlay ([data-nv-feedback]) are not steps.
 *              (3) Label order: aria-label → title → visible text (≤40 chars) →
 *              data-ui-part → tag; `part` is the closest data-ui-part.
 */
const MAX_ROUTES = 5;
const MAX_CLICKS = 10;
const MAX_LABEL = 40;
const OVERLAY = '[data-nv-feedback]';
// What a click "means": the nearest control, else the nearest named part.
const CONTROL = 'button, a[href], summary, label, input, select, textarea, [contenteditable=""], [contenteditable="true"],' + ' [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="checkbox"],' + ' [role="radio"], [role="switch"], [role="combobox"], [data-ui-part]';
const FIELD = 'input, select, textarea, [contenteditable=""], [contenteditable="true"]';
let installed = false;
let installedRouter = null;
let routeHandler = null;
let routes = [];
let clicks = [];
const pathOf = url => String(url ?? '').split(/[?#]/)[0];
const clip = text => {
  const clean = String(text ?? '').replace(/\s+/g, ' ').trim();
  return clean.length > MAX_LABEL ? `${clean.slice(0, MAX_LABEL - 1)}…` : clean;
};
function safeClosest(el, selector) {
  try {
    return el.closest(selector);
  } catch {
    return null;
  }
}
function selectorsFor(isClinic) {
  let selectors;
  try {
    selectors = privateSelectors({
      isClinic
    }) ?? [];
  } catch {
    selectors = [];
  }
  return selectors.includes('[data-nv-private]') ? selectors : ['[data-nv-private]', ...selectors];
}

/** True when `el` sits in a private zone (clinic superset by default: over-hiding a label is harmless). */
function isPrivateElement(el, {
  isClinic = true
} = {}) {
  if (!el || el.nodeType !== 1) return false;
  return selectorsFor(isClinic).some(selector => Boolean(safeClosest(el, selector)));
}

/**
 * Privacy of a click/tap target. A field is judged by where it sits, not by being a field: the clinic list
 * covers every input/textarea for its VALUE in the screenshot, while its <label> / placeholder is safe to name.
 */
function isPrivateTarget(el, {
  isClinic = true
} = {}) {
  if (!el || el.nodeType !== 1) return false;
  if (el.matches(FIELD)) return isPrivateElement(el.parentElement, {
    isClinic
  });
  return isPrivateElement(el, {
    isClinic
  });
}

/** The element a click/tap on `el` stands for: the nearest control or named part, else `el` itself. */
function clickTarget(el) {
  if (!el) return null;
  const element = el.nodeType === 1 ? el : el.parentElement;
  if (!element) return null;
  return safeClosest(element, CONTROL) ?? element;
}
function fieldLabel(el) {
  const own = el.labels?.[0];
  if (own) {
    // a <label> wrapping a textarea holds its initial value as text: read it without the fields
    return clip(textOf(own));
  }
  return clip(el.getAttribute('placeholder'));
}

/**
 * Human label of `el`, never a value: aria-label → title → visible text (≤40) → data-ui-part → tag.
 * A field (input/select/textarea/contenteditable) is named by its <label> or placeholder, never by its content.
 * `isPrivate` → only the part / tag (an aria-label or a text could carry a patient's name).
 */
function elementLabel(el, {
  isPrivate = false
} = {}) {
  if (!el || el.nodeType !== 1) return '';
  const part = el.getAttribute('data-ui-part') ?? safeClosest(el, '[data-ui-part]')?.getAttribute('data-ui-part');
  const tag = el.tagName.toLowerCase();
  if (isPrivate) return part || tag;
  const aria = clip(el.getAttribute('aria-label'));
  if (aria) return aria;
  const title = clip(el.getAttribute('title'));
  if (title) return title;
  if (el.matches(FIELD)) {
    return fieldLabel(el) || part || tag;
  }
  const text = clip(textOf(el));
  return text || part || tag;
}

// Visible text without fields (a textarea's textContent is its initial value) and without private zones inside.
function textOf(el) {
  const hidden = [FIELD, ...selectorsFor(true)];
  const holds = root => hidden.some(selector => {
    try {
      return Boolean(root.querySelector(selector));
    } catch {
      return false;
    }
  });
  if (!holds(el)) return el.textContent;
  const copy = el.cloneNode(true);
  for (const selector of hidden) {
    try {
      copy.querySelectorAll(selector).forEach(node => node.remove());
    } catch {
      // an invalid selector hides nothing
    }
  }
  return copy.textContent;
}
function recordRoute() {
  const router = installedRouter;
  const url = pathOf(router?.currentURL);
  if (!url) return;
  const name = router?.currentRouteName ?? null;
  if (routes.length && routes.at(-1).url === url) {
    routes[routes.length - 1] = {
      url,
      name,
      at: Date.now()
    }; // same page, new query params
    return;
  }
  routes.push({
    url,
    name,
    at: Date.now()
  });
  if (routes.length > MAX_ROUTES) routes.shift();
}
function onClick(event) {
  const origin = event.target;
  const element = origin?.nodeType === 1 ? origin : origin?.parentElement;
  if (!element || safeClosest(element, OVERLAY)) return;
  const target = clickTarget(element);
  if (!target) return;
  const part = safeClosest(target, '[data-ui-part]')?.getAttribute('data-ui-part') ?? null;
  const label = elementLabel(target, {
    isPrivate: isPrivateTarget(target)
  });
  clicks.push({
    label,
    part,
    at: Date.now()
  });
  if (clicks.length > MAX_CLICKS) clicks.shift();
}

/** Starts recording pages (on `router`, the router service) and clicks. Idempotent; a new router replaces the old. */
function installFeedbackTrail(router) {
  if (typeof document === 'undefined') return;
  if (installed && installedRouter === (router ?? null)) return;
  if (installed) uninstallFeedbackTrail();
  installedRouter = router ?? null;
  if (router && typeof router.on === 'function') {
    routeHandler = () => recordRoute();
    router.on('routeDidChange', routeHandler);
  }
  recordRoute();
  document.addEventListener('click', onClick, true);
  installed = true;
}

/** Stops recording and forgets the trail. */
function uninstallFeedbackTrail() {
  if (typeof document !== 'undefined') document.removeEventListener('click', onClick, true);
  if (installedRouter && routeHandler && typeof installedRouter.off === 'function') {
    installedRouter.off('routeDidChange', routeHandler);
  }
  installedRouter = null;
  routeHandler = null;
  routes = [];
  clicks = [];
  installed = false;
}

/** Snapshot of the trail (copies: the caller may keep it while the user goes on clicking). */
function feedbackTrail() {
  return {
    installed,
    routes: routes.map(route => ({
      ...route
    })),
    clicks: clicks.map(click => ({
      ...click
    }))
  };
}

export { clickTarget, elementLabel, feedbackTrail, installFeedbackTrail, isPrivateElement, isPrivateTarget, uninstallFeedbackTrail };
//# sourceMappingURL=nv-feedback-trail.js.map
